import React, { useState, useEffect, useRef } from 'react';
import { X, Phone, Mail, DollarSign, CreditCard, Info, Clock, Search, User, Scissors, CheckCircle, CalendarCheck, XCircle, Edit2, Receipt } from 'lucide-react';
import { useConfiguracoesHorarios } from '../../hooks/useConfiguracoesHorarios';
import api from '../../services/api';

// 🔥 FUNÇÕES DE CORES ATUALIZADAS
const getStatusBgColor = (status) => {
  switch(status) {
    case 'confirmado':
      return 'bg-green-100';
    case 'agendado':
      return 'bg-blue-100';
    case 'em_atendimento':
      return 'bg-yellow-100';
    case 'concluido':
      return 'bg-gray-100';
    case 'cancelado':
      return 'bg-red-100';
    default:
      return 'bg-gray-100';
  }
};

const getStatusTextColor = (status) => {
  switch(status) {
    case 'confirmado':
      return 'text-green-700';
    case 'agendado':
      return 'text-blue-700';
    case 'em_atendimento':
      return 'text-yellow-700';
    case 'concluido':
      return 'text-gray-700';
    case 'cancelado':
      return 'text-red-700';
    default:
      return 'text-gray-700';
  }
};

const getStatusLargeIcon = (status) => {
  switch(status) {
    case 'confirmado':
      return <CheckCircle className="w-6 h-6 text-green-600" />;
    case 'agendado':
      return <Clock className="w-6 h-6 text-blue-600" />;
    case 'em_atendimento':
      return <Scissors className="w-6 h-6 text-yellow-600" />;
    case 'concluido':
      return <CalendarCheck className="w-6 h-6 text-gray-600" />;
    case 'cancelado':
      return <XCircle className="w-6 h-6 text-red-600" />;
    default:
      return <Clock className="w-6 h-6 text-gray-600" />;
  }
};

const getStatusName = (status) => {
  switch(status) {
    case 'confirmado':
      return 'Confirmado';
    case 'agendado':
      return 'Agendado';
    case 'em_atendimento':
      return 'Em Atendimento';
    case 'concluido':
      return 'Concluído';
    case 'cancelado':
      return 'Cancelado';
    default:
      return 'Agendado';
  }
};

const ModalAgendamento = ({
  show,
  onClose,
  agendamentoSelecionado,
  formData,
  setFormData,
  onSalvar,
  onExcluir,
  profissionais,
  servicos,
  clientes,
  horarioInicial,
  onRefreshData
}) => {
  const { horariosDisponiveis, loading, config } = useConfiguracoesHorarios();
  
  const [statusAnterior, setStatusAnterior] = useState(null);
  const [servicoSelecionado, setServicoSelecionado] = useState(null);
  const [profissionalSelecionado, setProfissionalSelecionado] = useState(null);
  const [comissaoCalculada, setComissaoCalculada] = useState(0);
  const [percentualComissao, setPercentualComissao] = useState(0);
  const [mostrarPagamento, setMostrarPagamento] = useState(false);
  const [formaPagamento, setFormaPagamento] = useState('dinheiro');
  const [bandeiraCartao, setBandeiraCartao] = useState('');
  const [parcelas, setParcelas] = useState(1);
  const [horariosDoDia, setHorariosDoDia] = useState([]);
  const [finalizandoComanda, setFinalizandoComanda] = useState(false);
  
  // 🔥 ESTADOS PARA VALOR PERSONALIZADO
  const [valorPersonalizado, setValorPersonalizado] = useState(null);
  const [editandoValor, setEditandoValor] = useState(false);
  const [valorInput, setValorInput] = useState('');
  
  // 🔥 ESTADOS PARA COMISSÃO PERSONALIZADA
  const [comissaoPersonalizada, setComissaoPersonalizada] = useState(null);
  const [editandoComissao, setEditandoComissao] = useState(false);
  const [comissaoInput, setComissaoInput] = useState('');
  
  // 🔥 ESTADOS DA COMANDA
  const [mostrarComanda, setMostrarComanda] = useState(false);
  const [servicosAbertos, setServicosAbertos] = useState([]);
  const [carregandoComanda, setCarregandoComanda] = useState(false);
  
  // Estados para proteger os dados do clique
  const [dadosDoClique, setDadosDoClique] = useState(false);
  const [profissionalDoClique, setProfissionalDoClique] = useState(null);
  
  // Estados para busca de cliente
  const [buscaCliente, setBuscaCliente] = useState('');
  const [mostrarDropdownCliente, setMostrarDropdownCliente] = useState(false);
  const [clientesFiltrados, setClientesFiltrados] = useState([]);
  const dropdownClienteRef = useRef(null);

  // Estados para busca de serviço
  const [buscaServico, setBuscaServico] = useState('');
  const [mostrarDropdownServico, setMostrarDropdownServico] = useState(false);
  const [servicosFiltrados, setServicosFiltrados] = useState([]);
  const dropdownServicoRef = useRef(null);

  // 🔥 FUNÇÃO PARA EXTRAIR E CONVERTER HORA DE UTC PARA LOCAL
  const extrairHoraLocal = (dataHoraStr) => {
    if (!dataHoraStr) return '';
    
    // Se for string ISO "2026-06-21T13:00:00.000Z"
    if (typeof dataHoraStr === 'string') {
      // Extrair a hora UTC
      const match = dataHoraStr.match(/T(\d{2}):(\d{2})/);
      if (match) {
        let horaUTC = parseInt(match[1]);
        const minuto = parseInt(match[2]);
        
        // Converter UTC para Local (subtrair 3 horas)
        let horaLocal = horaUTC - 3;
        if (horaLocal < 0) {
          horaLocal = horaLocal + 24;
        }
        
        return `${String(horaLocal).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;
      }
    }
    
    // Se já estiver no formato local, retornar como está
    return dataHoraStr;
  };

  // 🔥 FUNÇÃO PARA EXTRAIR DATA/HORA LOCAL (Brasília) DE UMA STRING UTC
  const extrairDataHoraLocal = (dataHoraStr) => {
    if (!dataHoraStr) return null;
    
    let dataHora = dataHoraStr;
    
    // Se for string ISO "2026-06-19T13:30:00.000Z"
    if (typeof dataHora === 'string') {
      // Remover 'Z' se existir
      if (dataHora.includes('Z')) {
        dataHora = dataHora.replace('Z', '');
      }
      
      // Remover milissegundos se existir
      if (dataHora.includes('.')) {
        dataHora = dataHora.split('.')[0];
      }
      
      // Converter T para espaço
      if (dataHora.includes('T')) {
        dataHora = dataHora.replace('T', ' ');
      }
      
      // 🔥 CONVERTER UTC PARA LOCAL (subtrair 3 horas)
      const match = dataHora.match(/(\d{4}-\d{2}-\d{2})\s(\d{2}):(\d{2}):(\d{2})/);
      if (match) {
        const [_, data, horaStr, minutoStr, segundoStr] = match;
        let horaNum = parseInt(horaStr);
        const minutoNum = parseInt(minutoStr);
        const segundoNum = parseInt(segundoStr);
        
        // Subtrair 3 horas (UTC-3 = Brasília)
        horaNum = horaNum - 3;
        
        // Ajustar se hora ficou negativa
        let diaAjustado = data;
        if (horaNum < 0) {
          horaNum = horaNum + 24;
          // Se passou da meia-noite, ajustar a data
          const dataParts = data.split('-');
          const ano = parseInt(dataParts[0]);
          const mes = parseInt(dataParts[1]) - 1;
          const dia = parseInt(dataParts[2]);
          
          const dataObj = new Date(ano, mes, dia);
          dataObj.setDate(dataObj.getDate() - 1);
          
          diaAjustado = `${dataObj.getFullYear()}-${String(dataObj.getMonth() + 1).padStart(2, '0')}-${String(dataObj.getDate()).padStart(2, '0')}`;
        }
        
        const horaLocal = String(horaNum).padStart(2, '0');
        const minutoLocal = String(minutoNum).padStart(2, '0');
        const segundoLocal = String(segundoNum).padStart(2, '0');
        
        const resultado = `${diaAjustado} ${horaLocal}:${minutoLocal}:${segundoLocal}`;
        console.log(`🔄 UTC -> Local: ${dataHoraStr} -> ${resultado}`);
        return resultado;
      }
    }
    
    // Se já estiver no formato local, retornar como está
    return dataHora;
  };

  // 🔥 FUNÇÃO PARA BUSCAR SERVIÇOS EM ABERTO DO CLIENTE - CORRIGIDA
  const buscarServicosEmAberto = async (clienteId) => {
    if (!clienteId) return;
    
    setCarregandoComanda(true);
    try {
      const response = await api.get('/agendamentos');
      const todosAgendamentos = response.data;
      
      const agendamentosDoCliente = todosAgendamentos.filter(a => 
        a.cliente_id === parseInt(clienteId) && 
        a.status !== 'concluido' && 
        a.status !== 'cancelado'
      );
      
      const servicosAbertosFormatados = agendamentosDoCliente.map(a => {
        const dataHoraOriginal = a.data_hora;
        let dataFormatada = '';
        let horarioOriginal = '';
        
        if (dataHoraOriginal) {
          const str = String(dataHoraOriginal);
          // Extrair a data
          const dataMatch = str.match(/(\d{4}-\d{2}-\d{2})/);
          if (dataMatch) {
            dataFormatada = dataMatch[1];
          }
          
          // 🔥 CORREÇÃO: Extrair e converter a hora de UTC para Local
          // A hora original no banco está em UTC, precisamos converter para Brasília (UTC-3)
          if (typeof dataHoraOriginal === 'string') {
            const horaMatch = dataHoraOriginal.match(/T(\d{2}):(\d{2})/);
            if (horaMatch) {
              let horaUTC = parseInt(horaMatch[1]);
              const minuto = parseInt(horaMatch[2]);
              
              // Subtrair 3 horas (UTC-3 = Brasília)
              let horaLocal = horaUTC - 3;
              if (horaLocal < 0) {
                horaLocal = horaLocal + 24;
              }
              
              horarioOriginal = `${String(horaLocal).padStart(2, '0')}:${String(minuto).padStart(2, '0')}`;
            }
          } else {
            // Fallback: tentar extrair de outros formatos
            const horaMatch = str.match(/(\d{2}:\d{2})/);
            if (horaMatch) {
              horarioOriginal = horaMatch[1];
            }
          }
        }
        
        return {
          id: a.id,
          servico_id: a.servico_id,
          servico_nome: a.servico_nome || 'Serviço',
          servico_preco: parseFloat(a.valor_personalizado || a.servico_preco || a.valor || 0),
          servico_preco_original: parseFloat(a.servico_preco || 0),
          data_agendamento: dataFormatada,
          horario_original: horarioOriginal, // 🔥 AGORA EM HORÁRIO LOCAL
          data_hora_original: dataHoraOriginal,
          profissional_nome: a.funcionario_nome || 'Profissional',
          profissional_id: a.funcionario_id,
          status: a.status,
          selected: false,
          percentual_comissao: a.percentual_comissao || 30,
          valor: a.valor || a.servico_preco || 0,
          valor_personalizado: a.valor_personalizado || null,
          cliente_id: a.cliente_id
        };
      });
      
      setServicosAbertos(servicosAbertosFormatados);
      
    } catch (error) {
      console.error('Erro ao buscar serviços em aberto:', error);
      alert('Erro ao carregar comanda do cliente');
    } finally {
      setCarregandoComanda(false);
    }
  };

  const toggleSelecionarServico = (servico) => {
    setServicosAbertos(prev => prev.map(s => 
      s.id === servico.id ? { ...s, selected: !s.selected } : s
    ));
  };

  const selecionarTodos = () => {
    setServicosAbertos(prev => prev.map(s => ({ ...s, selected: true })));
  };

  const desmarcarTodos = () => {
    setServicosAbertos(prev => prev.map(s => ({ ...s, selected: false })));
  };

  const getTotalSelecionadosComanda = () => {
    return servicosAbertos.filter(s => s.selected).reduce((sum, s) => sum + s.servico_preco, 0);
  };

  // 🔥 FUNÇÃO PARA FINALIZAR SERVIÇOS DA COMANDA - CORRIGIDA
  const aplicarServicosSelecionados = async () => {
    const selecionados = servicosAbertos.filter(s => s.selected);
    
    if (selecionados.length === 0) {
      alert('Selecione pelo menos um serviço');
      return;
    }

    if (!formaPagamento) {
      alert('Selecione a forma de pagamento');
      return;
    }

    if (formaPagamento === 'credito_parcelado' && (!parcelas || parcelas < 1)) {
      alert('Selecione o número de parcelas');
      return;
    }

    if ((formaPagamento === 'debito' || formaPagamento === 'credito' || formaPagamento === 'credito_parcelado') && !bandeiraCartao) {
      alert('Selecione a bandeira do cartão');
      return;
    }

    setFinalizandoComanda(true);

    try {
      let totalValor = 0;
      let totalComissao = 0;
      const idsFinalizados = [];

      for (const servico of selecionados) {
        const response = await api.get(`/agendamentos/${servico.id}`);
        const agendamentoOriginal = response.data;
        
        console.log(`📋 Agendamento ${servico.id} original:`, {
          id: agendamentoOriginal.id,
          data_hora: agendamentoOriginal.data_hora,
          status: agendamentoOriginal.status,
          funcionario_id: agendamentoOriginal.funcionario_id
        });

        const percComissao = servico.percentual_comissao || 30;
        const valorComissao = (servico.servico_preco * percComissao) / 100;

        // 🔥 CONVERTER UTC PARA LOCAL ANTES DE ENVIAR
        const dataHoraOriginal = agendamentoOriginal.data_hora;
        const dataHoraLocal = extrairDataHoraLocal(dataHoraOriginal);

        console.log(`🕐 Data/hora original: ${dataHoraOriginal}`);
        console.log(`🕐 Data/hora local (enviando): ${dataHoraLocal}`);

        const dadosAtualizados = {
          status: 'concluido',
          forma_pagamento: formaPagamento,
          data_pagamento: new Date().toISOString(),
          parcelas: parcelas || 1,
          bandeira_cartao: bandeiraCartao || null,
          valor_comissao: valorComissao,
          percentual_comissao: percComissao,
          valor: servico.servico_preco,
          data_hora: dataHoraLocal, // 🔥 ENVIAR LOCAL
          cliente_id: agendamentoOriginal.cliente_id,
          funcionario_id: agendamentoOriginal.funcionario_id,
          servico_id: agendamentoOriginal.servico_id
        };

        if (servico.servico_preco !== servico.servico_preco_original) {
          dadosAtualizados.valor_personalizado = servico.servico_preco;
          dadosAtualizados.valor_original = servico.servico_preco_original;
        }

        console.log(`📤 Enviando data_hora: ${dadosAtualizados.data_hora}`);

        await api.put(`/agendamentos/${servico.id}`, dadosAtualizados);

        totalValor += servico.servico_preco;
        totalComissao += valorComissao;
        idsFinalizados.push(servico.id);
      }

      alert(`${idsFinalizados.length} serviço(s) finalizado(s) com sucesso!\nTotal: R$ ${totalValor.toFixed(2)}\nComissão Total: R$ ${totalComissao.toFixed(2)}`);

      const eventoConcluido = new CustomEvent('agendamentoConcluido', {
        detail: {
          agendamentoId: null,
          funcionarioId: null,
          funcionarioNome: 'Múltiplos',
          servicoNome: `${idsFinalizados.length} serviços`,
          valor: totalValor,
          comissao: totalComissao,
          data: new Date().toISOString(),
          pagamento: { forma_pagamento: formaPagamento },
          multiplos: true,
          quantidade: idsFinalizados.length,
          ids: idsFinalizados
        }
      });
      window.dispatchEvent(eventoConcluido);

      window.dispatchEvent(new CustomEvent('refreshAgendamentos'));

      if (onRefreshData) {
        await onRefreshData();
      }

      setServicosAbertos([]);
      setMostrarComanda(false);
      setMostrarPagamento(false);
      onClose();

    } catch (error) {
      console.error('❌ Erro ao finalizar serviços:', error);
      alert(`Erro ao finalizar os serviços: ${error.response?.data?.error || error.message}`);
    } finally {
      setFinalizandoComanda(false);
    }
  };

  // Lista de bandeiras de cartão
  const bandeirasCartao = [
    'Visa', 'Mastercard', 'American Express', 'Elo', 'Hipercard',
    'Diners Club', 'Discover', 'Aura', 'JCB', 'Sorocred',
    'Credsystem', 'Policard', 'Agiplan', 'Banes Card', 'Calcard',
    'Up (Alelo)', 'Sodexo', 'Ticket Restaurante', 'Vale (Alelo)'
  ];

  // Formas de pagamento
  const formasPagamento = [
    { id: 'dinheiro', nome: 'Dinheiro' },
    { id: 'pix', nome: 'PIX' },
    { id: 'debito', nome: 'Cartão de Débito' },
    { id: 'credito', nome: 'Cartão de Crédito' },
    { id: 'credito_parcelado', nome: 'Cartão de Crédito Parcelado' },
    { id: 'transferencia', nome: 'Transferência Bancária' },
    { id: 'fiado', nome: 'Fiado (Crediário)' }
  ];

  // RESETAR O MODAL QUANDO FECHAR
  useEffect(() => {
    if (!show) {
      setBuscaCliente('');
      setBuscaServico('');
      setServicoSelecionado(null);
      setProfissionalSelecionado(null);
      setMostrarPagamento(false);
      setFormaPagamento('dinheiro');
      setBandeiraCartao('');
      setParcelas(1);
      setDadosDoClique(false);
      setProfissionalDoClique(null);
      setValorPersonalizado(null);
      setEditandoValor(false);
      setValorInput('');
      setComissaoPersonalizada(null);
      setEditandoComissao(false);
      setComissaoInput('');
      setMostrarComanda(false);
      setServicosAbertos([]);
      setFinalizandoComanda(false);
    }
  }, [show]);

  // CARREGAR COMANDA QUANDO CLIENTE É SELECIONADO
  useEffect(() => {
    if (mostrarComanda && formData.clienteId) {
      buscarServicosEmAberto(formData.clienteId);
    }
  }, [mostrarComanda, formData.clienteId]);

  // Atualizar dados quando o agendamentoSelecionado mudar
  useEffect(() => {
    if (show && agendamentoSelecionado) {
      setDadosDoClique(false);
      setProfissionalDoClique(null);
      
      setBuscaCliente('');
      setBuscaServico('');
      setServicoSelecionado(null);
      setProfissionalSelecionado(null);
      
      if (agendamentoSelecionado.cliente_id) {
        const clienteEncontrado = clientes?.find(c => c.id === agendamentoSelecionado.cliente_id);
        if (clienteEncontrado) {
          setBuscaCliente(clienteEncontrado.nome);
        }
      }
      
      if (agendamentoSelecionado.servico_id) {
        const servicoEncontrado = servicos?.find(s => s.id === agendamentoSelecionado.servico_id);
        if (servicoEncontrado) {
          setBuscaServico(servicoEncontrado.nome);
          setServicoSelecionado(servicoEncontrado);
          if (agendamentoSelecionado.valor_personalizado) {
            setValorPersonalizado(agendamentoSelecionado.valor_personalizado);
          } else {
            setValorPersonalizado(null);
          }
          if (agendamentoSelecionado.comissao_personalizada) {
            setComissaoPersonalizada(agendamentoSelecionado.comissao_personalizada);
          } else {
            setComissaoPersonalizada(null);
          }
        }
      }
      
      if (agendamentoSelecionado.funcionario_id) {
        const profissionalEncontrado = profissionais?.find(p => p.id === agendamentoSelecionado.funcionario_id);
        if (profissionalEncontrado) {
          setProfissionalSelecionado(profissionalEncontrado);
        }
      }
      
      setStatusAnterior(agendamentoSelecionado.status);
    }
  }, [agendamentoSelecionado, show, clientes, servicos, profissionais]);

  // 🔥 DEFINIR HORÁRIO BASEADO NO CLIQUE DO CALENDÁRIO
  useEffect(() => {
    if (show && horarioInicial && !agendamentoSelecionado) {
      console.log('🎯 Definindo horário do clique:', horarioInicial);
      setDadosDoClique(true);
      
      if (formData.profissionalId) {
        const prof = profissionais?.find(p => p.id === parseInt(formData.profissionalId));
        if (prof) {
          setProfissionalDoClique(prof);
          setProfissionalSelecionado(prof);
        }
      }
      
      setTimeout(() => {
        if (formData.hora !== horarioInicial) {
          console.log('⏰ Aplicando horário com delay:', horarioInicial);
          setFormData(prev => ({ ...prev, hora: horarioInicial }));
        }
      }, 50);
    }
  }, [show, horarioInicial, agendamentoSelecionado, formData.profissionalId, formData.hora, profissionais, setFormData]);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownClienteRef.current && !dropdownClienteRef.current.contains(event.target)) {
        setMostrarDropdownCliente(false);
      }
      if (dropdownServicoRef.current && !dropdownServicoRef.current.contains(event.target)) {
        setMostrarDropdownServico(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filtrar clientes
  useEffect(() => {
    if (buscaCliente.trim() === '') {
      setClientesFiltrados(clientes || []);
    } else {
      const termoBusca = buscaCliente.toLowerCase();
      const filtrados = (clientes || []).filter(cliente => 
        cliente.nome.toLowerCase().includes(termoBusca) ||
        (cliente.telefone && cliente.telefone.includes(termoBusca)) ||
        (cliente.email && cliente.email.toLowerCase().includes(termoBusca))
      );
      setClientesFiltrados(filtrados);
    }
  }, [buscaCliente, clientes]);

  // Filtrar serviços
  useEffect(() => {
    if (buscaServico.trim() === '') {
      setServicosFiltrados(servicos || []);
    } else {
      const termoBusca = buscaServico.toLowerCase();
      const filtrados = (servicos || []).filter(servico => 
        servico.nome.toLowerCase().includes(termoBusca) ||
        (servico.categoria && servico.categoria.toLowerCase().includes(termoBusca)) ||
        (servico.preco && servico.preco.toString().includes(termoBusca))
      );
      setServicosFiltrados(filtrados);
    }
  }, [buscaServico, servicos]);

  // Atualizar busca do cliente
  useEffect(() => {
    if (formData.clienteId && clientes && show) {
      const clienteSelecionado = clientes.find(c => c.id === parseInt(formData.clienteId));
      if (clienteSelecionado) {
        setBuscaCliente(clienteSelecionado.nome);
      }
    }
  }, [formData.clienteId, clientes, show]);

  // Atualizar busca do serviço
  useEffect(() => {
    if (dadosDoClique) return;
    
    if (formData.servicoId && servicos && show) {
      const servicoSelecionadoObj = servicos.find(s => s.id === parseInt(formData.servicoId));
      if (servicoSelecionadoObj) {
        setBuscaServico(servicoSelecionadoObj.nome);
        setServicoSelecionado(servicoSelecionadoObj);
        if (!agendamentoSelecionado) {
          setValorPersonalizado(null);
          setEditandoValor(false);
          setComissaoPersonalizada(null);
          setEditandoComissao(false);
        }
      }
    }
  }, [formData.servicoId, servicos, show, dadosDoClique, agendamentoSelecionado]);

  // Gerar horários
  useEffect(() => {
    if (formData.data) {
      setHorariosDoDia(horariosDisponiveis);
    }
  }, [formData.data, horariosDisponiveis]);

  // Atualizar profissional selecionado
  useEffect(() => {
    if (dadosDoClique && profissionalDoClique) {
      if (profissionalSelecionado?.id !== profissionalDoClique.id) {
        setProfissionalSelecionado(profissionalDoClique);
      }
      return;
    }
    
    if (formData.profissionalId && profissionais && show) {
      const prof = profissionais.find(p => p.id === parseInt(formData.profissionalId));
      setProfissionalSelecionado(prof);
    } else {
      setProfissionalSelecionado(null);
    }
  }, [formData.profissionalId, profissionais, show, dadosDoClique, profissionalDoClique, profissionalSelecionado]);

  // 🔥 Calcular comissão
  useEffect(() => {
    if (servicoSelecionado && profissionalSelecionado) {
      let percComissao;
      if (comissaoPersonalizada !== null) {
        percComissao = comissaoPersonalizada;
      } else {
        percComissao = servicoSelecionado.comissao_percentual || 
                       profissionalSelecionado.comissao_percentual || 
                       30;
      }
      
      setPercentualComissao(percComissao);
      
      const valorBase = valorPersonalizado !== null ? valorPersonalizado : parseFloat(servicoSelecionado.preco);
      const valorComissao = (valorBase * percComissao) / 100;
      setComissaoCalculada(valorComissao);
    } else {
      setComissaoCalculada(0);
      setPercentualComissao(0);
    }
  }, [servicoSelecionado, profissionalSelecionado, valorPersonalizado, comissaoPersonalizada]);

  // Salvar status anterior
  useEffect(() => {
    if (agendamentoSelecionado && show) {
      setStatusAnterior(agendamentoSelecionado.status);
    }
  }, [agendamentoSelecionado, show]);

  // Mostrar pagamento
  useEffect(() => {
    if (formData.status === 'concluido') {
      setMostrarPagamento(true);
    } else {
      setMostrarPagamento(false);
    }
  }, [formData.status]);

  const selecionarCliente = (cliente) => {
    setBuscaCliente(cliente.nome);
    setFormData({ ...formData, clienteId: cliente.id });
    setMostrarDropdownCliente(false);
    setServicosAbertos([]);
    setMostrarComanda(false);
  };

  const selecionarServico = (servico) => {
    setBuscaServico(servico.nome);
    setFormData({ ...formData, servicoId: servico.id });
    setServicoSelecionado(servico);
    setMostrarDropdownServico(false);
    
    if (!agendamentoSelecionado) {
      setValorPersonalizado(null);
      setEditandoValor(false);
      setComissaoPersonalizada(null);
      setEditandoComissao(false);
    }
    
    if (dadosDoClique) {
      setDadosDoClique(false);
    }
  };

  const limparCliente = () => {
    setBuscaCliente('');
    setFormData({ ...formData, clienteId: '' });
    setMostrarDropdownCliente(false);
    setMostrarComanda(false);
    setServicosAbertos([]);
    setBuscaServico('');
    setServicoSelecionado(null);
    setValorPersonalizado(null);
  };

  const limparServico = () => {
    setBuscaServico('');
    setFormData({ ...formData, servicoId: '' });
    setServicoSelecionado(null);
    setMostrarDropdownServico(false);
    
    if (!agendamentoSelecionado) {
      setValorPersonalizado(null);
      setEditandoValor(false);
      setComissaoPersonalizada(null);
      setEditandoComissao(false);
    }
  };

  const handleStatusChange = (novoStatus) => {
    setFormData(prev => ({ 
      ...prev, 
      status: novoStatus
    }));
  };

  // 🔥 FUNÇÕES DE VALOR
  const handleAlterarValor = () => {
    setEditandoValor(true);
    const valorAtual = valorPersonalizado !== null ? valorPersonalizado : (servicoSelecionado?.preco || 0);
    setValorInput(valorAtual.toString());
  };

  const handleSalvarValorPersonalizado = () => {
    let novoValor = parseFloat(valorInput.replace(',', '.'));
    if (isNaN(novoValor) || novoValor < 0) {
      novoValor = servicoSelecionado?.preco || 0;
    }
    setValorPersonalizado(novoValor);
    setEditandoValor(false);
  };

  const handleCancelarEdicaoValor = () => {
    setEditandoValor(false);
    setValorInput('');
  };

  const handleResetarValor = () => {
    setValorPersonalizado(null);
    setEditandoValor(false);
  };

  // 🔥 FUNÇÕES DE COMISSÃO
  const handleAlterarComissao = () => {
    setEditandoComissao(true);
    const comissaoAtual = comissaoPersonalizada !== null ? comissaoPersonalizada : percentualComissao;
    setComissaoInput(comissaoAtual.toString());
  };

  const handleSalvarComissaoPersonalizada = () => {
    let novoPercentual = parseFloat(comissaoInput.replace(',', '.'));
    if (isNaN(novoPercentual) || novoPercentual < 0) {
      const percPadrao = servicoSelecionado?.comissao_percentual || 
                        profissionalSelecionado?.comissao_percentual || 
                        30;
      novoPercentual = percPadrao;
      setComissaoPersonalizada(null);
    } else if (novoPercentual > 100) {
      novoPercentual = 100;
      setComissaoPersonalizada(novoPercentual);
    } else {
      setComissaoPersonalizada(novoPercentual);
    }
    setEditandoComissao(false);
    setComissaoInput('');
  };

  const handleCancelarEdicaoComissao = () => {
    setEditandoComissao(false);
    setComissaoInput('');
  };

  const handleResetarComissao = () => {
    setComissaoPersonalizada(null);
    setEditandoComissao(false);
    setComissaoInput('');
  };

  const handleProfissionalChange = (e) => {
    const profissionalId = e.target.value;
    setFormData({ ...formData, profissionalId });
    
    if (dadosDoClique) {
      setDadosDoClique(false);
      setProfissionalDoClique(null);
    }
  };

  const handleHorarioChange = (e) => {
    const novoHorario = e.target.value;
    setFormData({ ...formData, hora: novoHorario });
    
    if (dadosDoClique) {
      setDadosDoClique(false);
    }
  };

  const handleSalvar = async () => {
    if (!formData.clienteId) {
      alert('Selecione um cliente');
      return;
    }

    if (!formData.servicoId) {
      alert('Selecione um serviço');
      return;
    }

    if (!formData.profissionalId) {
      alert('Selecione um profissional');
      return;
    }

    if (!formData.hora) {
      alert('Selecione um horário');
      return;
    }

    const statusMudouParaConcluido = 
      agendamentoSelecionado && 
      formData.status === 'concluido' && 
      statusAnterior !== 'concluido';

    if (formData.status === 'concluido' && !formaPagamento) {
      alert('Selecione a forma de pagamento');
      return;
    }

    if (formaPagamento === 'credito_parcelado' && (!parcelas || parcelas < 1)) {
      alert('Selecione o número de parcelas');
      return;
    }

    if ((formaPagamento === 'debito' || formaPagamento === 'credito' || formaPagamento === 'credito_parcelado') && !bandeiraCartao) {
      alert('Selecione a bandeira do cartão');
      return;
    }

    const valorOriginalServico = servicoSelecionado?.preco || 0;
    const valorFinal = valorPersonalizado !== null ? valorPersonalizado : valorOriginalServico;

    let percentualComissaoFinal;
    if (comissaoPersonalizada !== null) {
      percentualComissaoFinal = comissaoPersonalizada;
    } else {
      percentualComissaoFinal = servicoSelecionado?.comissao_percentual || 
                              profissionalSelecionado?.comissao_percentual || 
                              30;
    }

    const dadosCompletos = {
      cliente_id: parseInt(formData.clienteId),
      funcionario_id: parseInt(formData.profissionalId),
      servico_id: parseInt(formData.servicoId),
      data_hora: `${formData.data}T${formData.hora}:00`,
      status: formData.status,
      observacoes: formData.observacoes || '',
      valor: valorFinal,
      valor_original: valorOriginalServico,
      valor_personalizado: valorPersonalizado !== null ? valorFinal : null,
      comissao_personalizada: comissaoPersonalizada !== null ? comissaoPersonalizada : null,
      percentual_comissao: percentualComissaoFinal,
      forma_pagamento: formaPagamento || null,
      bandeira_cartao: bandeiraCartao || null,
      parcelas: parcelas || 1,
      data_pagamento: formData.status === 'concluido' ? new Date().toISOString() : null
    };

    try {
      await onSalvar(dadosCompletos);

      if (statusMudouParaConcluido) {
        const evento = new CustomEvent('agendamentoConcluido', {
          detail: {
            agendamentoId: agendamentoSelecionado?.id,
            funcionarioId: parseInt(formData.profissionalId),
            funcionarioNome: profissionalSelecionado?.nome,
            servicoNome: servicoSelecionado?.nome,
            valor: valorFinal,
            valor_original: valorOriginalServico,
            comissao: (valorFinal * percentualComissaoFinal) / 100,
            comissao_percentual: percentualComissaoFinal,
            data: formData.data,
            hora: formData.hora,
            pagamento: dadosCompletos
          }
        });
        window.dispatchEvent(evento);
      }

      setMostrarPagamento(false);
      onClose();
      
      if (onRefreshData) {
        await onRefreshData();
      }
      
    } catch (error) {
      console.error('❌ Erro ao salvar:', error);
      alert(`Erro ao salvar: ${error.response?.data?.error || error.message}`);
    }
  };

  const formatarPreco = (preco) => {
    if (preco === null || preco === undefined) return 'R$ 0,00';
    return `R$ ${parseFloat(preco).toFixed(2).replace('.', ',')}`;
  };

  const getValorExibido = () => {
    if (valorPersonalizado !== null) {
      return valorPersonalizado;
    }
    return servicoSelecionado?.preco || 0;
  };

  const getPercentualComissaoExibido = () => {
    if (comissaoPersonalizada !== null) {
      return comissaoPersonalizada;
    }
    return servicoSelecionado?.comissao_percentual || 
           profissionalSelecionado?.comissao_percentual || 
           30;
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${getStatusBgColor(formData.status)}`}>
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {getStatusLargeIcon(formData.status)}
            <h2 className="text-xl font-semibold">
              {agendamentoSelecionado ? 'Editar Agendamento' : 'Novo Agendamento'}
            </h2>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBgColor(formData.status)} ${getStatusTextColor(formData.status)} border`}>
              {getStatusName(formData.status)}
            </span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          {/* 🔥 SEÇÃO DA COMANDA */}
          {mostrarComanda && formData.clienteId && (
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200 overflow-hidden">
              <div className="bg-purple-100 px-4 py-3 border-b border-purple-200 flex justify-between items-center">
                <h3 className="font-semibold text-purple-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Comanda do Cliente - {buscaCliente}
                </h3>
                <div className="flex gap-2">
                  {servicosAbertos.some(s => s.selected) && (
                    <button onClick={desmarcarTodos} className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600">
                      Desmarcar Todos
                    </button>
                  )}
                  <button onClick={selecionarTodos} className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600">
                    Selecionar Todos
                  </button>
                </div>
              </div>
              
              <div className="p-4">
                {carregandoComanda ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500">Carregando serviços...</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4">
                      <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                        <Clock className="w-4 h-4 text-yellow-600" />
                        Selecione os serviços que deseja finalizar
                        {servicosAbertos.length > 0 && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">
                            {servicosAbertos.filter(s => s.selected).length} selecionado(s)
                          </span>
                        )}
                      </h4>
                      
                      {servicosAbertos.length === 0 ? (
                        <div className="bg-gray-50 rounded-lg p-4 text-center text-gray-500 text-sm">
                          Nenhum serviço em aberto para este cliente
                        </div>
                      ) : (
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                          {servicosAbertos.map(servico => (
                            <div 
                              key={servico.id} 
                              className={`bg-white rounded-lg p-3 border transition-all cursor-pointer hover:shadow-md ${
                                servico.selected ? 'border-purple-500 bg-purple-50' : 'border-gray-200'
                              }`}
                              onClick={() => toggleSelecionarServico(servico)}
                            >
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                                      servico.selected ? 'bg-purple-500 border-purple-500' : 'border-gray-300'
                                    }`}>
                                      {servico.selected && <CheckCircle className="w-3 h-3 text-white" />}
                                    </div>
                                    <div className="font-medium text-gray-900">{servico.servico_nome}</div>
                                  </div>
                                  <div className="text-xs text-gray-500 mt-1 ml-7">
                                    <div>📅 {servico.data_agendamento}</div>
                                    <div>🕐 {servico.horario_original || 'Horário não informado'}</div>
                                    <div>👤 {servico.profissional_nome}</div>
                                    <div className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs ${
                                      servico.status === 'agendado' ? 'bg-blue-100 text-blue-700' : 
                                      servico.status === 'confirmado' ? 'bg-green-100 text-green-700' :
                                      servico.status === 'em_atendimento' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>
                                      <span>{getStatusName(servico.status)}</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-green-600">{formatarPreco(servico.servico_preco)}</div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {servicosAbertos.length > 0 && (
                      <div className="mt-4 pt-4 border-t-2 border-purple-200">
                        <div className="flex justify-between items-center mb-3">
                          <span className="font-bold text-gray-800">Total Selecionado:</span>
                          <span className="text-2xl font-bold text-purple-600">{formatarPreco(getTotalSelecionadosComanda())}</span>
                        </div>
                        
                        {/* 🔥 FORMULÁRIO DE PAGAMENTO DA COMANDA */}
                        <div className="bg-white rounded-lg p-3 mb-3 border border-purple-200">
                          <h4 className="font-medium text-gray-700 mb-2 text-sm">Dados de Pagamento</h4>
                          <div className="space-y-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Forma de Pagamento *</label>
                              <select
                                className="input-field text-sm"
                                value={formaPagamento}
                                onChange={(e) => setFormaPagamento(e.target.value)}
                              >
                                {formasPagamento.map(fp => (
                                  <option key={fp.id} value={fp.id}>{fp.nome}</option>
                                ))}
                              </select>
                            </div>

                            {(formaPagamento === 'debito' || formaPagamento === 'credito' || formaPagamento === 'credito_parcelado') && (
                              <>
                                <div>
                                  <label className="block text-xs font-medium text-gray-600 mb-1">Bandeira do Cartão *</label>
                                  <select
                                    className="input-field text-sm"
                                    value={bandeiraCartao}
                                    onChange={(e) => setBandeiraCartao(e.target.value)}
                                  >
                                    <option value="">Selecione a bandeira...</option>
                                    {bandeirasCartao.map(bandeira => (
                                      <option key={bandeira} value={bandeira}>{bandeira}</option>
                                    ))}
                                  </select>
                                </div>

                                {formaPagamento === 'credito_parcelado' && (
                                  <div>
                                    <label className="block text-xs font-medium text-gray-600 mb-1">Parcelas</label>
                                    <select
                                      className="input-field text-sm"
                                      value={parcelas}
                                      onChange={(e) => setParcelas(parseInt(e.target.value))}
                                    >
                                      {[1,2,3,4,5,6,7,8,9,10,11,12].map(num => (
                                        <option key={num} value={num}>{num}x</option>
                                      ))}
                                    </select>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        <button
                          onClick={aplicarServicosSelecionados}
                          disabled={!servicosAbertos.some(s => s.selected) || finalizandoComanda}
                          className={`w-full py-2 rounded-lg transition-colors flex items-center justify-center gap-2 ${
                            servicosAbertos.some(s => s.selected) && !finalizandoComanda
                              ? 'bg-green-500 text-white hover:bg-green-600'
                              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          }`}
                        >
                          {finalizandoComanda ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                              Finalizando...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4" />
                              Finalizar {servicosAbertos.filter(s => s.selected).length} serviço(s)
                            </>
                          )}
                        </button>
                        <p className="text-xs text-gray-500 mt-2 text-center">
                          * Os serviços serão finalizados mantendo seus horários originais
                        </p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Cliente */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Cliente *
              </label>
              {formData.clienteId && !mostrarComanda && (
                <button
                  type="button"
                  onClick={() => {
                    setMostrarComanda(true);
                    buscarServicosEmAberto(formData.clienteId);
                  }}
                  className="flex items-center gap-2 px-3 py-1 rounded-lg text-sm bg-gray-100 text-gray-700 hover:bg-purple-50 hover:text-purple-600 transition-colors"
                >
                  <Receipt className="w-4 h-4" />
                  Ver Comanda
                  {servicosAbertos.length > 0 && (
                    <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                      {servicosAbertos.length}
                    </span>
                  )}
                </button>
              )}
              {mostrarComanda && (
                <button
                  type="button"
                  onClick={() => {
                    setMostrarComanda(false);
                    setServicosAbertos([]);
                  }}
                  className="flex items-center gap-2 px-3 py-1 rounded-lg text-sm bg-purple-100 text-purple-700 border border-purple-300"
                >
                  <X className="w-4 h-4" />
                  Fechar Comanda
                </button>
              )}
            </div>
            
            <div className="relative" ref={dropdownClienteRef}>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  className="input-field pl-10 pr-10"
                  placeholder="Digite o nome, telefone ou email do cliente..."
                  value={buscaCliente}
                  onChange={(e) => {
                    setBuscaCliente(e.target.value);
                    setMostrarDropdownCliente(true);
                    if (e.target.value === '') {
                      limparCliente();
                    }
                  }}
                  onFocus={() => setMostrarDropdownCliente(true)}
                  disabled={mostrarComanda}
                />
                {buscaCliente && !mostrarComanda && (
                  <button
                    onClick={limparCliente}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {mostrarDropdownCliente && clientesFiltrados.length > 0 && !mostrarComanda && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                  {clientesFiltrados.map(cliente => (
                    <div
                      key={cliente.id}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                      onClick={() => selecionarCliente(cliente)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white font-medium text-sm">
                          {cliente.nome.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{cliente.nome}</div>
                          <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                            {cliente.telefone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {cliente.telefone}
                              </span>
                            )}
                            {cliente.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {cliente.email}
                              </span>
                            )}
                          </div>
                        </div>
                        <User className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {formData.clienteId && !mostrarComanda && (
              <div className="mt-2 text-xs text-green-600 flex items-center gap-1">
                <User className="w-3 h-3" />
                Cliente selecionado: {buscaCliente}
              </div>
            )}
          </div>

          {/* Serviço */}
          {!mostrarComanda && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Serviço *
              </label>
              <div className="relative" ref={dropdownServicoRef}>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    className="input-field pl-10 pr-10"
                    placeholder="Digite o nome do serviço..."
                    value={buscaServico}
                    onChange={(e) => {
                      setBuscaServico(e.target.value);
                      setMostrarDropdownServico(true);
                      if (e.target.value === '') {
                        setFormData({ ...formData, servicoId: '' });
                        setServicoSelecionado(null);
                      }
                    }}
                    onFocus={() => setMostrarDropdownServico(true)}
                  />
                  {buscaServico && (
                    <button
                      onClick={limparServico}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {mostrarDropdownServico && servicosFiltrados.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                    {servicosFiltrados.map(servico => (
                      <div
                        key={servico.id}
                        className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0 transition-colors"
                        onClick={() => selecionarServico(servico)}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center text-white">
                            <Scissors className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{servico.nome}</div>
                            <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                              {servico.categoria && (
                                <span className="flex items-center gap-1">
                                  <span className="text-gray-400">📂</span>
                                  {servico.categoria}
                                </span>
                              )}
                              {servico.duracao_minutos && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {servico.duracao_minutos} min
                                </span>
                              )}
                              <span className="font-medium text-green-600">
                                {formatarPreco(servico.preco)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {formData.servicoId && servicoSelecionado && (
                <div className="mt-2 text-xs text-green-600 flex items-center gap-2">
                  <Scissors className="w-3 h-3" />
                  <span>Serviço selecionado: {buscaServico}</span>
                  <span className={`font-medium ${valorPersonalizado !== null ? 'text-purple-600 line-through text-gray-400' : 'text-blue-600'}`}>
                    {formatarPreco(servicoSelecionado.preco)}
                  </span>
                  {valorPersonalizado !== null && (
                    <span className="text-purple-600 font-bold">
                      → {formatarPreco(valorPersonalizado)}
                    </span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Profissional */}
          {!mostrarComanda && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profissional *
              </label>
              <select 
                className={`input-field ${dadosDoClique ? 'border-green-500 bg-green-50' : ''}`}
                value={formData.profissionalId}
                onChange={handleProfissionalChange}
              >
                <option value="">Selecione um profissional</option>
                {profissionais.map(prof => (
                  <option key={prof.id} value={prof.id}>{prof.nome}</option>
                ))}
              </select>
              {dadosDoClique && profissionalDoClique && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  Profissional selecionado automaticamente do calendário: {profissionalDoClique.nome}
                </p>
              )}
            </div>
          )}

          {/* 🔥 Informações de Preço e Comissão */}
          {servicoSelecionado && !mostrarComanda && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium text-blue-800 flex items-center gap-2">
                  <Info className="w-4 h-4" />
                  Informações do Serviço
                </h3>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm text-gray-600">Valor do Serviço</p>
                    {!editandoValor ? (
                      <button
                        onClick={handleAlterarValor}
                        className="flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                        Alterar
                      </button>
                    ) : (
                      <button
                        onClick={handleResetarValor}
                        className="flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded text-gray-500 hover:bg-gray-100 transition-colors"
                      >
                        <X className="w-3 h-3" />
                        Cancelar
                      </button>
                    )}
                  </div>
                  {editandoValor ? (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-gray-600 text-sm">R$</span>
                      <input
                        type="number"
                        step="0.01"
                        className="input-field w-28 text-sm font-bold"
                        value={valorInput}
                        onChange={(e) => setValorInput(e.target.value)}
                        autoFocus
                      />
                      <button
                        onClick={handleSalvarValorPersonalizado}
                        className="bg-green-500 text-white px-2 py-0.5 rounded text-xs"
                      >
                        Salvar
                      </button>
                      <button
                        onClick={handleCancelarEdicaoValor}
                        className="bg-gray-300 px-2 py-0.5 rounded text-xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <>
                      {valorPersonalizado !== null && (
                        <p className="text-sm text-gray-400 line-through">
                          {formatarPreco(servicoSelecionado.preco)}
                        </p>
                      )}
                      <p className={`text-lg font-bold ${valorPersonalizado !== null ? 'text-purple-600' : 'text-gray-900'}`}>
                        {formatarPreco(getValorExibido())}
                      </p>
                    </>
                  )}
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm text-gray-600">Comissão</p>
                    {!editandoComissao ? (
                      <button
                        onClick={handleAlterarComissao}
                        className="flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded text-purple-600 hover:bg-purple-50 transition-colors"
                      >
                        <Edit2 className="w-3 h-3" />
                        Alterar
                      </button>
                    ) : (
                      <button
                        onClick={handleResetarComissao}
                        className="flex items-center gap-1 text-xs bg-white px-2 py-0.5 rounded text-gray-500 hover:bg-gray-100 transition-colors"
                      >
                        <X className="w-3 h-3" />
                        Cancelar
                      </button>
                    )}
                  </div>
                  {editandoComissao ? (
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        className="input-field w-20 text-sm font-bold"
                        value={comissaoInput}
                        onChange={(e) => setComissaoInput(e.target.value)}
                        autoFocus
                      />
                      <span className="text-gray-600 text-sm">%</span>
                      <button
                        onClick={handleSalvarComissaoPersonalizada}
                        className="bg-green-500 text-white px-2 py-0.5 rounded text-xs"
                      >
                        Salvar
                      </button>
                      <button
                        onClick={handleCancelarEdicaoComissao}
                        className="bg-gray-300 px-2 py-0.5 rounded text-xs"
                      >
                        Cancelar
                      </button>
                    </div>
                  ) : (
                    <>
                      {comissaoPersonalizada !== null && (
                        <p className="text-sm text-gray-400 line-through">
                          {servicoSelecionado?.comissao_percentual || 
                           profissionalSelecionado?.comissao_percentual || 
                           30}%
                        </p>
                      )}
                      <p className={`text-lg font-bold ${comissaoPersonalizada !== null ? 'text-purple-600' : 'text-green-600'}`}>
                        {getPercentualComissaoExibido()}%
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatarPreco(comissaoCalculada)}
                      </p>
                    </>
                  )}
                </div>
              </div>
              
              {servicoSelecionado.duracao_minutos && (
                <div className="mt-2 pt-2 border-t border-blue-200">
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Duração estimada: {servicoSelecionado.duracao_minutos} minutos
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Data e Hora */}
          {!mostrarComanda && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Data *
                </label>
                <input 
                  type="date" 
                  className="input-field"
                  value={formData.data}
                  onChange={(e) => setFormData({...formData, data: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Horário *
                </label>
                <select
                  className={`input-field ${dadosDoClique ? 'border-green-500 bg-green-50' : ''}`}
                  value={formData.hora || ''}
                  onChange={handleHorarioChange}
                  required
                >
                  <option value="">Selecione um horário</option>
                  {horariosDoDia.map(horario => (
                    <option key={horario} value={horario}>
                      {horario} {dadosDoClique && horario === horarioInicial ? '✓ (Selecionado)' : ''}
                    </option>
                  ))}
                </select>
                {loading && (
                  <p className="text-xs text-gray-500 mt-1">Carregando horários...</p>
                )}
                {config && config.intervaloMinutos && (
                  <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Intervalo de {config.intervaloMinutos} minutos entre horários
                  </p>
                )}
                {dadosDoClique && horarioInicial && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Horário selecionado automaticamente do calendário: {horarioInicial}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Status */}
          {!mostrarComanda && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select 
                className={`input-field ${getStatusTextColor(formData.status)}`}
                value={formData.status}
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <option value="agendado">📅 Agendado (Azul)</option>
                <option value="confirmado">✅ Confirmado (Verde)</option>
                <option value="em_atendimento">⏳ Em Atendimento (Amarelo)</option>
                <option value="concluido">🎉 Concluído (Cinza)</option>
                <option value="cancelado">❌ Cancelado (Vermelho)</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Agendado: Azul | Confirmado: Verde | Em Atendimento: Amarelo | Concluído: Cinza | Cancelado: Vermelho
              </p>
            </div>
          )}

          {/* Observações */}
          {!mostrarComanda && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea 
                rows="3"
                className="input-field"
                placeholder="Observações sobre o atendimento..."
                value={formData.observacoes}
                onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
              />
            </div>
          )}

          {/* Informações adicionais */}
          {agendamentoSelecionado && !mostrarComanda && (
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <h3 className="font-medium text-gray-700">Informações do Cliente</h3>
              <p className="text-sm flex items-center gap-2">
                <Phone className="w-4 h-4 text-gray-400" />
                {agendamentoSelecionado.cliente_telefone || 'Não informado'}
              </p>
              <p className="text-sm flex items-center gap-2">
                <Mail className="w-4 h-4 text-gray-400" />
                {agendamentoSelecionado.cliente_email || 'Não informado'}
              </p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-6 flex justify-end gap-3">
          {agendamentoSelecionado && onExcluir && typeof onExcluir === 'function' && !mostrarComanda && (
            <button 
              onClick={() => onExcluir(agendamentoSelecionado.id)}
              className="btn-danger"
            >
              Excluir
            </button>
          )}
          <button 
            onClick={onClose}
            className="btn-secondary"
          >
            Cancelar
          </button>
          {!mostrarComanda && (
            <button 
              onClick={handleSalvar}
              className="btn-primary"
            >
              {agendamentoSelecionado ? 'Salvar Alterações' : 'Criar Agendamento'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalAgendamento;
